import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';

import type { RouterOutputs } from '~/utils/api';

export type SplitResult = RouterOutputs['cadenceTemplate']['splitResults'][number];
type SplitArmResult = SplitResult['arms'][number];
type SplitMetricVerdict = SplitResult['accept'];

/** Kept in step with SPLIT_ACCEPT_MATURITY_DAYS / SPLIT_REPLY_MATURITY_DAYS in splitResults.service. */
const MATURITY_NOTE = 'Accept rate counts invites at least 7 days old; reply rate, at least 14 days old.';

const pct = (r: { matured: number; hits: number }) =>
  r.matured === 0 ? '–' : `${Math.round((r.hits / r.matured) * 1000) / 10}%`;

/**
 * An A/B split arm's caption (POS-2568): its branch letter + configured share, and — on the template
 * page, once results load — how many contacts it got, how many were invited, and its accept / reply
 * rates over invites old enough to count. Read-only: shares are set in the split's step editor.
 */
export function SplitArmCard({
  label,
  percent,
  result,
}: {
  label: string;
  percent: number;
  result: SplitArmResult | null;
}) {
  return (
    <div
      data-testid="split-arm-card"
      className="min-w-36 rounded-lg border border-purple-200 bg-white px-2 py-1 text-[11px] text-lynch-700 shadow-sm dark:border-purple-500/40 dark:bg-charade-900 dark:text-lynch-200">
      <div className="whitespace-nowrap font-semibold text-purple-700 dark:text-purple-300">
        {label} · {percent}%
      </div>
      {result != null && (
        <Tooltip tooltip={MATURITY_NOTE}>
          <div data-testid="split-arm-stats" className="whitespace-nowrap">
            {result.assigned} in · {result.sent} invited
          </div>
          <div className="whitespace-nowrap">
            Accept <strong>{pct(result.accept)}</strong> · Reply <strong>{pct(result.reply)}</strong>
          </div>
        </Tooltip>
      )}
    </div>
  );
}

function verdictText(metric: 'accept' | 'reply', v: SplitMetricVerdict): string {
  if (v.status === 'not_enough_data') return `${metric}: not enough data yet (${v.needPerArm} more per branch)`;
  if (v.status === 'winner') return `${metric}: ${v.leader} wins by ${v.diffPts} pts`;
  return `${metric}: no clear winner yet (${v.leader} +${v.diffPts} pts)`;
}

/**
 * The split's "confident yet?" line, under the split node: per metric, a winner only when a
 * two-proportion test clears 95%, and "not enough data" below the per-branch sample floor.
 */
export function SplitVerdictPill({ result }: { result: SplitResult }) {
  const confident = result.accept.status === 'winner' || result.reply.status === 'winner';
  return (
    <Tooltip tooltip={MATURITY_NOTE}>
      <div
        data-testid="split-verdict"
        className={cn(
          'whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold shadow-sm first-letter:uppercase',
          confident
            ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300'
            : 'bg-lynch-100 text-lynch-700 dark:bg-slate-800 dark:text-lynch-300',
        )}>
        {verdictText('accept', result.accept)} · {verdictText('reply', result.reply)}
      </div>
    </Tooltip>
  );
}
